"use client";

import { CustomButton, FlexCenter, FlexCol } from "@/components";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import DownloadPdf from "@/components/pdfTemplates/downloadPdf";
import ResultContent from "@/components/resultContent";
import { IBaseLabelValue } from "@/interfaces";
import {
  IDrivingLicenseStatus,
  IRawPurchasedDrivingLicenseStatusInquiryResData,
} from "@/interfaces/inquiries/vehicle";
import { cn, toJalali } from "@/lib";
import { routes } from "@/routes";
import { useRouter } from "next/navigation";

const valueOrDash = (value?: string | null) =>
  value && value !== "-" ? value : "-";
const formatDate = (value?: string) => {
  if (!value || value === "-" || value === "نامشخص") return "-";
  return toJalali(value) || value;
};
const hasLicenseData = (license: IDrivingLicenseStatus) =>
  Boolean(license.licenseNumber && license.licenseNumber !== "-");

const licenseRows = (license: IDrivingLicenseStatus): IBaseLabelValue[] => [
  { label: "نام", value: valueOrDash(license.firstname) },
  { label: "نام خانوادگی", value: valueOrDash(license.lastname) },
  { label: "کد ملی", value: valueOrDash(license.nationalCode) },
  { label: "شماره گواهینامه", value: valueOrDash(license.licenseNumber) },
  { label: "نوع گواهینامه", value: valueOrDash(license.licenseType) },
  { label: "وضعیت گواهینامه", value: valueOrDash(license.licenseStatus) },
  { label: "تاریخ صدور", value: formatDate(license.issueDate) },
  { label: "تاریخ ثبت درخواست", value: formatDate(license.RequestDateTime) },
  { label: "تاریخ تایید چاپ", value: formatDate(license.PrintConfirmDateTime) },
  {
    label: "تاریخ چاپ گواهینامه",
    value: formatDate(license.PrintLicenseDateTime),
  },
  { label: "بارکد", value: valueOrDash(license.barCode) },
  {
    label: "مدت اعتبار",
    value:
      license.licenseValidityPeriod && license.licenseValidityPeriod !== "-"
        ? `${license.licenseValidityPeriod} سال`
        : "-",
  },
];

const DrivingLicenseStatusInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedDrivingLicenseStatusInquiryResData;
}) => {
  const router = useRouter();
  const payload = (data as any)?.data ?? {};
  const result = payload?.data ?? {};
  const input = payload?.input ?? (data as any)?.user_input ?? {};
  const licenses = Array.isArray(result?.licenses)
    ? result.licenses.filter(hasLicenseData)
    : [];
  const inputContent: IBaseLabelValue[] = [
    {
      label: "کد ملی",
      value: valueOrDash(input.national_code || input.national_id),
    },
    {
      label: "شماره همراه",
      value: valueOrDash(input.mobile || input.mobileNumber),
    },
  ];
  const pdfResultContent: IBaseLabelValue[] = [
    ...inputContent,
    ...(result?.licenseStatus?.description
      ? [{ label: "وضعیت درخواست", value: result.licenseStatus.description }]
      : []),
    ...licenses.flatMap((license: IDrivingLicenseStatus, index: number) =>
      licenseRows(license).map((item) => ({
        label: `گواهینامه ${index + 1} - ${item.label}`,
        value: item.value,
      })),
    ),
  ];
  const pdfFileName = `استعلام گواهینامه و وضعیت ${valueOrDash(
    input.national_code || input.national_id,
  )}`;

  return (
    <div className="shadow-main from-sky-bliss relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-6 sm:p-8">
        <div className="mb-8 flex items-center justify-between gap-4">
          <h2 className="font-bold lg:text-lg">
            نتیجه استعلام گواهینامه و وضعیت
          </h2>
          <DownloadPdf
            id="driving-license-status-download-pdf"
            resultContent={pdfResultContent}
            tableTitle="نتیجه استعلام گواهینامه و وضعیت"
            fileName={pdfFileName}
          />
        </div>
        <InputDataContent
          content={inputContent}
          className="mb-6 p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />
        {result?.licenseStatus?.description && (
          <p className="text-secondary-main mb-6 text-center text-sm">
            {result.licenseStatus.description}
          </p>
        )}
        {licenses.length ? (
          licenses.map((license: IDrivingLicenseStatus, index: number) => (
            <ResultContent
              key={`${license.licenseNumber}-${index}`}
              title={`گواهینامه ${index + 1}`}
              content={licenseRows(license)}
            />
          ))
        ) : (
          <p className="mb-8 text-center text-sm text-gray-500">
            اطلاعات گواهینامه‌ای برای نمایش وجود ندارد.
          </p>
        )}
        <FlexCenter className="flex flex-col justify-center gap-4 md:flex-row">
          <DownloadPdf
            id="driving-license-status-download-pdf"
            resultContent={pdfResultContent}
            tableTitle="نتیجه استعلام گواهینامه و وضعیت"
            fileName={pdfFileName}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs font-medium lg:text-sm xl:text-base"
          />
          <CustomButton
            id="details-driving-license-status-back"
            className="w-fit bg-gray-500"
            onClick={() => router.push(routes.main.INQUIRY_HISTORY.primaryPath)}
          >
            بازگشت
          </CustomButton>
          <CustomButton
            id="details-driving-license-status-new-request"
            onClick={() =>
              router.push(routes.services.Driving_License_Status.rewritePath)
            }
            className={cn("bg-primary-main w-fit px-8! text-white")}
          >
            استعلام جدید
          </CustomButton>
        </FlexCenter>
      </FlexCol>
    </div>
  );
};

export default DrivingLicenseStatusInquiryDetails;
